// Shared payroll shift engine + preprocessing pipeline
// Used by PayrollTab and StaffPortalPage so totals always match

export const DEFAULT_ABANDONED_SHIFT_HOURS = 18;
export const DEFAULT_LUNCH_MINUTES = 60;

export function toDate(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v?.toDate === "function") return v.toDate();
  if (typeof v?.seconds === "number") return new Date(v.seconds * 1000);
  if (typeof v === "number") return new Date(v < 1e12 ? v * 1000 : v);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function toSec(v) {
  if (!v) return null;
  if (typeof v?.seconds === "number") return v.seconds;
  if (typeof v?.toDate === "function") return Math.floor(v.toDate().getTime() / 1000);
  if (v instanceof Date) return Math.floor(v.getTime() / 1000);
  if (typeof v === "number") return v < 1e12 ? Math.floor(v) : Math.floor(v / 1000);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : Math.floor(d.getTime() / 1000);
}

export function round2(n) {
  return Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100;
}

function hoursBetween(a, b) {
  if (!a || !b) return 0;
  let ms = b.getTime() - a.getTime();
  if (ms < 0) ms += 24 * 3600000;
  return ms > 0 ? ms / 3600000 : 0;
}

export function getShiftLunchMinutes(shift, fallbackMinutes = DEFAULT_LUNCH_MINUTES) {
  const direct = Number(shift?.lunchMin ?? shift?.lunchMinutes ?? shift?.lunch);
  if (Number.isFinite(direct) && direct >= 0) return direct;
  if (typeof shift?.lunchTaken === "boolean") {
    return shift.lunchTaken ? fallbackMinutes : 0;
  }
  return fallbackMinutes;
}

export function endOfDayMsForClockIn(ciSeconds) {
  const d = new Date(ciSeconds * 1000);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

export function isLegacyEndOfDayAutoOut(shift, abandonedHours = DEFAULT_ABANDONED_SHIFT_HOURS) {
  const ciSec = toSec(shift?.clockIn);
  const coSec = toSec(shift?.clockOut);
  const actualSec = toSec(shift?.actualClockOut);
  const thresholdSec = toSec(shift?.thresholdClockOut);

  if (typeof ciSec !== 'number' || typeof coSec !== 'number') return false;
  if (typeof actualSec === 'number' || typeof thresholdSec === 'number' || shift?.autoClockedOutAtThreshold) return false;

  const ci = new Date(ciSec * 1000);
  const co = new Date(coSec * 1000);

  const sameCalendarDay =
    ci.getFullYear() === co.getFullYear() &&
    ci.getMonth() === co.getMonth() &&
    ci.getDate() === co.getDate();

  if (!(sameCalendarDay && co.getHours() === 23 && co.getMinutes() === 59)) return false;

  const rawHours = (coSec - ciSec) / 3600;
  return rawHours > Math.max(1, Number(abandonedHours) || DEFAULT_ABANDONED_SHIFT_HOURS);
}

export function getThresholdCutoffSec(shift, abandonedHours = DEFAULT_ABANDONED_SHIFT_HOURS) {
  const ciSec = toSec(shift?.clockIn);
  if (typeof ciSec !== 'number') return null;
  return ciSec + Math.max(1, Number(abandonedHours) || DEFAULT_ABANDONED_SHIFT_HOURS) * 3600;
}

export function getDisplayClockOutSec(shift, abandonedHours = DEFAULT_ABANDONED_SHIFT_HOURS) {
  const ciSec = toSec(shift?.clockIn);
  const coSec = toSec(shift?.clockOut);
  const actualSec = toSec(shift?.actualClockOut);
  const thresholdSec = toSec(shift?.thresholdClockOut);
  const currentThresholdSec = getThresholdCutoffSec(shift, abandonedHours);

  if (shift?.active === false && typeof coSec !== 'number' && typeof actualSec !== 'number' && typeof thresholdSec !== 'number') {
    return null;
  }

  if (shift?.autoClockedOutAtThreshold || typeof thresholdSec === 'number') {
    return currentThresholdSec;
  }

  if (typeof coSec === 'number') {
    if (isLegacyEndOfDayAutoOut(shift, abandonedHours)) return currentThresholdSec;

    if (typeof ciSec === 'number' && typeof actualSec !== 'number') {
      const rawHours = (coSec - ciSec) / 3600;
      if (rawHours > Math.max(1, Number(abandonedHours) || DEFAULT_ABANDONED_SHIFT_HOURS)) {
        return currentThresholdSec;
      }
    }

    return coSec;
  }

  if (typeof actualSec === 'number') return actualSec;
  return null;
}

export function buildClockOutDate(ciDate, shift, abandonedHours = DEFAULT_ABANDONED_SHIFT_HOURS) {
  const outSec = getDisplayClockOutSec(shift, abandonedHours);
  if (typeof outSec !== 'number') {
    const d = new Date(ciDate);
    d.setHours(23, 59, 0, 0);
    return d;
  }
  let co = new Date(outSec * 1000);
  if (co < ciDate) {
    co = new Date(co.getTime() + 24 * 3600000);
  }
  return co;
}

export function dedupePayrollRows(items, getEffectiveOutSec = null) {
  const map = new Map();

  const minuteBucket = (sec) => {
    if (!sec) return '';
    const d = new Date(sec * 1000);
    return [
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, '0'),
      String(d.getDate()).padStart(2, '0'),
      String(d.getHours()).padStart(2, '0'),
      String(d.getMinutes()).padStart(2, '0'),
    ].join('-');
  };

  for (const s of items || []) {
    const ciSec = toSec(s?.clockIn);
    if (!ciSec) continue;

    const effectiveOutSec = typeof getEffectiveOutSec === 'function'
      ? getEffectiveOutSec(s)
      : getDisplayClockOutSec(s, DEFAULT_ABANDONED_SHIFT_HOURS) ?? Math.floor(endOfDayMsForClockIn(ciSec) / 1000);

    const key = [
      String(s.name || '').trim().toLowerCase(),
      String(s.role || '').trim().toLowerCase(),
      String(s.store || s.storeId || s.storeName || '').trim().toLowerCase(),
      minuteBucket(ciSec),
      minuteBucket(effectiveOutSec),
    ].join('|');

    const existing = map.get(key);
    if (!existing) {
      map.set(key, s);
      continue;
    }

    const existingEdited = Boolean(existing.oldClockIn || existing.oldClockOut || existing.reason || existing.editedAt || existing.editedBy || existing.updatedAt || existing.modifiedAt);
    const currentEdited = Boolean(s.oldClockIn || s.oldClockOut || s.reason || s.editedAt || s.editedBy || s.updatedAt || s.modifiedAt);

    const existingHasRealOut = Boolean(toSec(existing.clockOut));
    const currentHasRealOut = Boolean(toSec(s.clockOut));

    const existingSourceRank = existing?.__source === 'nested' ? 2 : 1;
    const currentSourceRank = s?.__source === 'nested' ? 2 : 1;

    const existingClockInSec = toSec(existing.clockIn) || 0;
    const currentClockInSec = toSec(s.clockIn) || 0;

    if (
      (!existingEdited && currentEdited) ||
      (!existingHasRealOut && currentHasRealOut) ||
      (currentSourceRank > existingSourceRank) ||
      (currentClockInSec > existingClockInSec)
    ) {
      map.set(key, s);
    }
  }

  return Array.from(map.values()).sort(
    (a, b) => (toSec(a.clockIn) || 0) - (toSec(b.clockIn) || 0)
  );
}

export function getLatestOpenShiftState(
  shifts,
  abandonedHours = DEFAULT_ABANDONED_SHIFT_HOURS
) {
  const open = (shifts || [])
    .filter((s) => toDate(s?.clockIn) && !getDisplayClockOutSec(s, abandonedHours))
    .sort(
      (a, b) =>
        (toDate(b.clockIn)?.getTime?.() || 0) -
        (toDate(a.clockIn)?.getTime?.() || 0)
    );

  if (!open.length) return null;

  const shift = open[0];
  const now = new Date();
  const start = toDate(shift.clockIn);
  const hrs = hoursBetween(start, now);

  if (hrs > abandonedHours) {
    return { abandoned: true, shift };
  }

  return { abandoned: false, shift };
}

export function computePaidHours(
  shift,
  lunchMinutes = DEFAULT_LUNCH_MINUTES,
  abandonedHours = DEFAULT_ABANDONED_SHIFT_HOURS
) {
  const start = toDate(shift?.clockIn);
  if (!start) return 0;
  const end = buildClockOutDate(start, shift, abandonedHours);
  if (!end) return 0;

  const rawHours = hoursBetween(start, end);
  const lunchHours = Math.max(0, getShiftLunchMinutes(shift, lunchMinutes)) / 60;

  return round2(Math.max(0, rawHours - lunchHours));
}

export function buildPayrollSummary(
  shifts,
  lunchMinutes = DEFAULT_LUNCH_MINUTES,
  abandonedHours = DEFAULT_ABANDONED_SHIFT_HOURS
) {
  let total = 0;

  (shifts || []).forEach((shift) => {
    total += computePaidHours(shift, lunchMinutes, abandonedHours);
  });

  return round2(total);
}
